import React, { useEffect, useState } from 'react';
import {
  fetchFacebookPages,
  assignFacebookPageToClient,
  unassignFacebookPageFromClient,
  refreshFacebookPagesFromApi,
  verifyFacebookToken,
  syncInstagramFromPage,
} from '../services/api';
import {
  Box,
  Select,
  MenuItem,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { SHOW_INSTAGRAM_UI } from '../config/featureFlags';
import ConfirmDialog from './ConfirmDialog';

export default function FacebookPageSelector({ client, onUpdate }) {
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(
    client.facebook_page_id || ''
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [unassignConfirmOpen, setUnassignConfirmOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [syncingIg, setSyncingIg] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setVerifyResult(null);
    setLoading(true);
    setError('');
    fetchFacebookPages(client?.id)
      .then(setPages)
      .catch((err) => {
        console.error('Failed to fetch Facebook pages:', err);
        setError('Failed to fetch Facebook pages. Please reconnect Facebook.');
        setPages([]);
      })
      .finally(() => setLoading(false));
  }, [client?.id]);

  const handleRefresh = async () => {
    setLoading(true);
    setError('');
    try {
      await refreshFacebookPagesFromApi();
      const refreshedPages = await fetchFacebookPages(client?.id);
      setPages(refreshedPages);
    } catch (err) {
      console.error('Failed to refresh Facebook pages:', err);
      setError('Failed to refresh Facebook pages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = () => {
    if (!client?.id || !client?.facebook_page_id) return;
    setUnassignConfirmOpen(true);
  };

  const handleConfirmUnassign = async () => {
    if (!client?.id || !client?.facebook_page_id) return;
    setUnassigning(true);
    setError('');
    try {
      await unassignFacebookPageFromClient(client.id);
      setSelectedPageId('');
      setSuccess('Page unassigned.');
      setVerifyResult(null);
      setUnassignConfirmOpen(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to unassign page.');
    } finally {
      setUnassigning(false);
    }
  };

  const handleSyncInstagram = async () => {
    if (!client?.id || !client?.facebook_page_id) return;
    setSyncingIg(true);
    setError('');
    try {
      const result = await syncInstagramFromPage(client.id);
      setSuccess(result.message || 'Instagram linked. Next Facebook post will also go to IG.');
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to link Instagram.');
    } finally {
      setSyncingIg(false);
    }
  };

  const handleVerifyToken = async () => {
    if (!client?.id) return;
    setVerifying(true);
    setVerifyResult(null);
    setError('');
    try {
      const result = await verifyFacebookToken(client.id);
      setVerifyResult(result);
    } catch (err) {
      setVerifyResult({ valid: false, error: err.message || 'Failed to verify' });
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const page = pages.find((p) => p.id === selectedPageId);
      await assignFacebookPageToClient(client.id, {
        facebook_page_id: page.id,
        facebook_page_name: page.name,
      });
      setSuccess('Page assigned!');
      setVerifyResult(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to assign Facebook page:', err);
      setError('Failed to assign Facebook page. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1">
        <b>Facebook Page:</b>
      </Typography>
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <Select
          value={selectedPageId}
          onChange={(e) => setSelectedPageId(e.target.value)}
          displayEmpty
          fullWidth
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Select a Page</MenuItem>
          {pages.map((page) => (
            <MenuItem key={page.id} value={page.id}>
              {page.name}
            </MenuItem>
          ))}
        </Select>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedPageId || saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button onClick={handleRefresh} variant="outlined" disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
        {SHOW_INSTAGRAM_UI && (
          <Button
            onClick={handleSyncInstagram}
            variant="outlined"
            disabled={syncingIg || !client?.facebook_page_id}
            title="Link Instagram to this Page (fix dual-publish if it didn't work)"
          >
            {syncingIg ? 'Linking...' : 'Link Instagram'}
          </Button>
        )}
        <Button
          onClick={handleVerifyToken}
          variant="outlined"
          disabled={verifying || !client?.facebook_page_id}
        >
          {verifying ? 'Verifying...' : 'Verify Token'}
        </Button>
        <Button
          onClick={handleUnassign}
          variant="outlined"
          color="secondary"
          disabled={unassigning || !client?.facebook_page_id}
        >
          {unassigning ? 'Unassigning...' : 'Unassign Page'}
        </Button>
      </Box>
      {verifyResult && (
        <Typography
          sx={{ mt: 1 }}
          color={
            verifyResult.valid === true && verifyResult.has_pages_read_engagement === false
              ? 'warning.main'
              : verifyResult.valid === true
              ? 'success.main'
              : verifyResult.valid === false
              ? 'error.main'
              : 'text.secondary'
          }
        >
          {verifyResult.valid === true
            ? verifyResult.has_pages_read_engagement === false
              ? `Token valid for ${verifyResult.page_name || 'page'}, but missing pages_read_engagement — disconnect and reconnect Facebook, then Backfill engagement in Analytics.`
              : `Token valid for ${verifyResult.page_name || 'page'} (pages_read_engagement granted)`
            : verifyResult.valid === false
            ? `Token invalid: ${verifyResult.error || 'Unknown error'}`
            : verifyResult.error || 'Could not verify'}
        </Typography>
      )}
      {success && <Typography color="success.main">{success}</Typography>}
      {client.facebook_page_name && (
        <Typography sx={{ mt: 1 }}>
          <b>Current:</b> {client.facebook_page_name}
        </Typography>
      )}
      {error && <Typography color="error.main">{error}</Typography>}

      <ConfirmDialog
        open={unassignConfirmOpen}
        title="Unassign Facebook page"
        message={`Unassign "${client.facebook_page_name}" from this client? You can re-assign later.`}
        confirmLabel="Unassign"
        confirmColor="warning"
        loading={unassigning}
        onCancel={() => {
          if (!unassigning) setUnassignConfirmOpen(false);
        }}
        onConfirm={handleConfirmUnassign}
      />
    </Box>
  );
}
